import { z } from 'zod'

/**
 * Модель с ответом на запрос глобальной конфигурации
 */
const modelConfigOnrampProviderLimit = z.object({
  /**
   * Лимит на одну операцию, ₽
   */
  perTx: z.number(),
  available: z.boolean(),
})

/**
 * Персональный месячный лимит онрампа. Приходит только тем, у кого включена новая схема
 * (`onrampV2Enabled`), у остальных — null: месячный блок не показываем.
 */
const modelConfigOnrampMonthlyLimit = z.object({
  /**
   * Лимит на календарный месяц либо скользящие 30 дней, ₽. Бэк сам выбирает значение
   * по привязке юзера (UPRID/ESIA или без неё)
   */
  limit: z.number(),
  /**
   * Накоплено за период, ₽
   */
  spent: z.number().nullish(),
  /**
   * Остаток лимита: заявка проходит, пока сумма <= remains, ₽
   */
  remains: z.number(),
  /**
   * Окно лимита: календарный месяц либо скользящие 30 дней — от него зависит подпись строки
   */
  window: z.enum(['calendar', 'rolling_30d']),
  /**
   * Разбивка накопленного по платёжным шлюзам новой схемы, ₽
   */
  byProvider: z.record(z.string(), z.number()).nullish(),
})

/**
 * Месячный лимит без собственного окна: карточный лимит платёжной системы и общий итог
 */
const modelConfigOnrampMonthlyTotal = z.object({
  limit: z.number(),
  spent: z.number(),
  remains: z.number(),
})

/**
 * Лимиты онрампа по классам: обычный (Kartoy) и VIP (Kartoy VIP)
 */
const modelConfigOnrampLimits = z.object({
  /**
   * Оборот у Kartoy, после которого открывается VIP, ₽
   */
  awStateThreshold: z.number(),
  /**
   * Накопленные траты юзера к порогу VIP, ₽
   */
  awStateSpent: z.number(),
  monthly: modelConfigOnrampMonthlyLimit.nullish(),
  /**
   * Карточный месячный лимит платёжной системы — общий с покупками по QR и карте
   */
  psCardMonthly: modelConfigOnrampMonthlyTotal.nullish(),
  /**
   * Общий месячный лимит: сумму считает бэк, фронт её не выводит
   */
  summary: modelConfigOnrampMonthlyTotal.nullish(),
  regular: modelConfigOnrampProviderLimit,
  vip: modelConfigOnrampProviderLimit,
})

/**
 * Лимиты выплат одного направления: цифры первичного провайдера.
 * Бэк отдаёт null там, где лимит в настройке не задан (0)
 */
const modelConfigPayoutDirectionLimit = z.object({
  /**
   * Максимум за операцию, ₽
   */
  perTx: z.number().nullish(),
  /**
   * Максимум за календарный месяц, ₽
   */
  monthly: z.number().nullish(),
  /**
   * Сколько выплат в день разрешено
   */
  perDayCount: z.number().nullish(),
  /**
   * На что навешан лимит: на реквизит (карта/телефон) или на пользователя целиком
   */
  limitBy: z.enum(['card', 'phone', 'user']),
})

export type ModelConfigPayoutDirectionLimit = z.infer<typeof modelConfigPayoutDirectionLimit>

/**
 * Лимиты вывода: карты РФ банков и СБП
 */
const modelConfigPayoutLimits = z.object({
  card: modelConfigPayoutDirectionLimit.nullish(),
  sbp: modelConfigPayoutDirectionLimit.nullish(),
})

const modelInnerConfigGlobalResponse = z.object({
  /**
   * Флаг, указывающий на то, что в данный момент проводится плановое обслуживание
   */
  isMaintenance: z.boolean(),
  /**
   * Платежи сейчас включены
   */
  paymentsEnabled: z.boolean(),
  /**
   * Можно ли продавать крипту
   */
  sellCrypto: z.boolean(),
  /**
   * Можно ли выплачивать по СБП
   */
  payoutSbp: z.boolean(),
  /**
   * Можно ли пополнять стрим
   */
  steamTopup: z.boolean(),
  /**
   * Можно ли пополнять телефон
   */
  phoneTopup: z.boolean(),
  /**
   * Нестабильная работа оплат по СБП
   */
  sbpUnstable: z.boolean(),
  /**
   * Доступность AML сервиса
   */
  amlEnabled: z.boolean().optional(),
  /**
   * Доступность проверки документов
   */
  docsVerification: z.boolean().optional(),
  /**
   * Sell Crypto Gateway
   * 0 -- Avatarix
   * 1 -- Feecutex
   */
  sellCryptoGateway: z.number(),
  /**
   * Платёжная система Platcore включена
   */
  platcoreEnabled: z.boolean().optional(),
  /**
   * Пополнение рублей включено
   */
  rubTopupEnabled: z.boolean().optional(),
  /**
   * Платёжная система UniUni включена
   */
  uniuniBuyCrypto: z.boolean().optional(),

  /**
   * Платёжная система Kartoy включена
   */
  awBuyCrypto: z.boolean().optional(),
  /**
   * Онрамп «по счёту/наличными» через Kartoy
   */
  awBuyCryptoCash: z.boolean().optional(),
  /**
   * Онрамп Kartoy доступен только по уровню платёжных сервисов РФ (currentPsLevel),
   * иначе достаточно базового KYC
   */
  onrampPsLevel: z.boolean().optional(),
  /**
   * Выплаты по СБП включены
   */
  uSbpPayoutEnabled: z.boolean().optional(),
  /**
   * Выплаты по карте включены
   */
  uCardPayoutEnabled: z.boolean().optional(),
  /**
   * Платёжная система включена
   */
  bankEnabled: z.boolean().optional(),
  /**
   * Сколько стран документа можно добавить в верификации. Нет в ответе - дефолт на клиенте
   */
  documentCountriesLimit: z.number().int().positive().optional(),
  /**
   * Ссылка на оффер банка
   */
  bankOfferUrl: z.string().optional(),
  /**
   * Ссылка на политику конфиденциальности банка
   */
  bankPrivacyUrl: z.string().optional(),
  /**
   * Новый флоу 2FA при логине (бэкенд возвращает TOTP_REQUIRED). По умолчанию false — legacy локальный флоу.
   */
  isNew2FaFlowEnabled: z.boolean().optional(),
  /**
   * Новая схема онрампа (роутинг по классу пользователя): тоггл с учётом канареек
   */
  onrampV2Enabled: z.boolean().optional(),
  onrampLimits: modelConfigOnrampLimits.optional(),
  payoutLimits: modelConfigPayoutLimits.optional(),
})

/**
 * Модель с ответом на запрос глобальной конфигурации
 */
export const modelConfigGlobalResponse = z.object({
  data: modelInnerConfigGlobalResponse.nullish(),
  errors: z.record(z.string(), z.any()),
  message: z.string().nullable(),
  status: z.string(),
})

/**
 * Тип модели с ответом на запрос глобальной конфигурации
 */
export type ModelInnerConfigGlobalResponse = z.infer<typeof modelInnerConfigGlobalResponse>

/**
 * Модель с ответом на запрос конфигурации приложения
 */

const modelInnerConfigAppResponse = z.object({
  update: z.boolean(),
  features: z.string().nullish(),
  links: z.object({
    linkTerms: z.string().nullish(),
    linkPolicy: z.string().nullish(),
    linkAML: z.string().nullish(),
    linkFaq: z.string().nullish(),
    linkCardWalletGuide: z.string().nullish(),

    linkStoreIos: z.string().nullish(),
    linkStoreAndroid: z.string().nullish(),
    linkSupport: z.string().nullish(),
    linkRateAppAndroid: z.string().nullish(),
    linkRateAppIos: z.string().nullish(),

    linkTelegram: z.string().nullish(),
    linkInstagram: z.string().nullish(),
    linkX: z.string().nullish(),
    linkBluesky: z.string().nullish(),
    linkVk: z.string().nullish(),
    linkDzen: z.string().nullish(),
    linkYoutube: z.string().nullish(),

    linkReferral: z.string().nullish(),
    linkReferralConditions: z.string().nullish(),

    linkSdk: z.string().nullish(),
    linkExampleApp: z.string().nullish(),
  }),
  ratingPopup: z.object({
    isEnabled: z.boolean(),
    frequencyHours: z.number(),
    snoozeDays: z.number(),
  }),
})

/**
 * Модель с ответом на запрос конфигурации приложения
 */
export const modelConfigAppResponse = z.object({
  data: modelInnerConfigAppResponse.nullish(),
  errors: z.record(z.string(), z.any()),
  message: z.string().nullable(),
  status: z.string(),
})

export type ModelInnerConfigAppResponse = z.infer<typeof modelConfigAppResponse>
export type MobileConfig = NonNullable<ModelInnerConfigAppResponse['data']>

const modelConfigCountryServiceLimit = z.object({
  currency: z.string(),
  source: z.string(),
  min: z.number().nullable(),
  max: z.number().nullable(),
})

export const ModelConfigCountrySlugs = z.enum([
  'alipay-sell',
  'aml-check',
  'link-pay',
  'mass-transfer',
  'mobile-topup',
  'qr-pay',
  'steam-topup',
  'bank-card-ru',
  'card-sell',
  'sbp-sell',
  'spb-sell',
  'fiat-topup',
  'fiat-sell',
])

export type ModelConfigCountrySlugsType = z.infer<typeof ModelConfigCountrySlugs>

const modelConfigCountryService = z.object({
  name: z.string().optional(),
  currency: z.string().optional(),
  // Бэк может прислать новый slug раньше клиента — неизвестные не валят весь /countries.
  slug: z.union([ModelConfigCountrySlugs, z.string()]).optional(),
  currencies: z.array(z.string()).optional(),
  limits: z.array(modelConfigCountryServiceLimit),
  fees: z.object({
    percent: z.number().nullable(),
    fix: z.number().nullable(),
  }),
  kyc: z.object({
    need: z.boolean(),
    level: z.number().nullable(),
    requirements: z.array(z.string()).optional(),
  }),
})

export type ModelConfigCountryService = z.infer<typeof modelConfigCountryService>
// Известные slug для typed maps; runtime-значение service.slug может быть шире.
export type ModelConfigCountryServiceSlug = ModelConfigCountrySlugsType

export function parseKnownCountryServiceSlug(
  slug: string | undefined | null,
): ModelConfigCountryServiceSlug | undefined {
  if (!slug) return undefined

  const parsed = ModelConfigCountrySlugs.safeParse(slug)

  return parsed.success ? parsed.data : undefined
}

export const modelConfigCountryItem = z.object({
  iso2: z.string(),
  iso3: z.string(),
  services: z.array(modelConfigCountryService),
})

export const modelConfigCountriesResponse = z.object({
  data: z.array(modelConfigCountryItem),
  errors: z.record(z.string(), z.any()),
  message: z.string().nullable(),
  status: z.string(),
})

export const modelConfigCountryByCodeResponse = z.object({
  data: modelConfigCountryItem.nullable(),
  errors: z.record(z.string(), z.any()),
  message: z.string().nullable(),
  status: z.string(),
})

export const modelConfigVcardLimit = z.object({
  level: z.number(),
  maxOneTime: z.number(),
  maxMonthly: z.number(),
})

export const modelConfigVcardLimitsResponse = z.object({
  data: z.array(modelConfigVcardLimit),
  errors: z.record(z.string(), z.any()),
  message: z.string().nullable(),
  status: z.string(),
})

export type ModelConfigCountryItem = z.infer<typeof modelConfigCountryItem>
export type ModelConfigVcardLimit = z.infer<typeof modelConfigVcardLimit>
